import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Upload, FileText, CheckCircle2, AlertTriangle, Loader2, ArrowRight, Clock } from "lucide-react";

const GOLD = "text-[#C9A961]";
const fmtMB = (b) => `${(b / 1024 / 1024).toFixed(1)} MB`;

function DocField({ api, doc, uploaded, onDone, disabled }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!["application/pdf", "image/jpeg", "image/png"].includes(f.type)) return toast.error("Only PDF, JPG or PNG files are accepted.");
    if (f.size > 10 * 1024 * 1024) return toast.error("Each document must be 10 MB or smaller.");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("doc_type", doc.key); fd.append("file", f);
      const { data } = await api.post("/director/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone(data.submission); toast.success(`${doc.label} uploaded`);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`kyc-doc-${doc.key}`} className={`rounded-sm border p-5 ${uploaded ? "border-[#C9A961]/60 bg-[#C9A961]/5" : "border-white/10 bg-black/40"}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-serif text-[#F5F5F0]">{doc.label} {doc.mandatory ? <span className={`font-grotesk text-[8px] uppercase tracking-[0.24em] ${GOLD} ml-1`}>Mandatory</span> : <span className="font-grotesk text-[8px] uppercase tracking-[0.24em] text-[#8A8A82] ml-1">Optional</span>}</div>
          <div className="font-body text-xs text-[#8A8A82] mt-1">{doc.hint}</div>
          {uploaded && <div className="mt-2 inline-flex items-center gap-1.5 font-body text-xs text-[#C9C9C2]" data-testid={`kyc-doc-${doc.key}-file`}><FileText size={12} className={GOLD} /> {uploaded.filename} · {fmtMB(uploaded.size)}</div>}
        </div>
        <button type="button" disabled={busy || disabled} onClick={() => ref.current?.click()} data-testid={`kyc-upload-${doc.key}`}
          className="shrink-0 inline-flex items-center gap-2 rounded-sm border border-[#C9A961]/50 text-[#C9A961] px-3 py-2 font-grotesk text-[9px] uppercase tracking-[0.22em] hover:bg-[#C9A961] hover:text-black transition-colors disabled:opacity-40">
          {busy ? <Loader2 size={11} className="animate-spin" /> : uploaded ? <CheckCircle2 size={11} /> : <Upload size={11} />} {uploaded ? "Replace" : "Upload"}
        </button>
        <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" onChange={pick} />
      </div>
    </div>
  );
}

export function DirectorKYCStep({ api, onVerifiedOrContinue, allowContinue = false }) {
  const [data, setData] = useState(null);
  const [decl, setDecl] = useState({});
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { setData((await api.get("/director/kyc")).data); } catch { /* silent */ } }, [api]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-center text-[#8A8A82] font-body py-10">Loading eKYC…</div>;
  const sub = data.submission, st = data.summary?.status, req = data.requirements;
  const uploadedBy = Object.fromEntries((sub?.documents || []).map((d) => [d.doc_type, d]));
  const mandatoryDone = req.documents.filter((d) => d.mandatory).every((d) => uploadedBy[d.key]);
  const locked = st === "pending_review" || st === "verified" || st === "escalated";

  const submit = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/director/kyc/submit", { declaration: agree, ...decl });
      toast.success(r.message); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Submission failed"); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="director-kyc-step">
      <div className="text-center">
        <ShieldCheck size={40} className={`mx-auto ${GOLD}`} />
        <span className={`block mt-5 font-grotesk text-[10px] uppercase tracking-[0.35em] ${GOLD}`}>· eKYC · {req.country} ·</span>
        <h1 className="font-serif text-4xl md:text-5xl mt-3 text-[#F5F5F0]">Identity Verification</h1>
        <p className="font-body text-[#C9C9C2] mt-5">Please upload the following documents as required for your country of residence.</p>
        <p className="font-body text-xs text-[#8A8A82] mt-2">PDF, JPG or PNG · maximum 10 MB per document · Mode {data.summary.mode}{data.summary.mode === "A" ? " — verification precedes the fee step" : " — fee step may proceed in parallel; activation follows verification"}</p>
      </div>

      {st === "pending_review" && (
        <div data-testid="kyc-status-pending" className="mt-8 rounded-sm border border-[#C9A961]/50 bg-[#C9A961]/5 p-6 text-center">
          <Clock size={22} className={`mx-auto ${GOLD}`} />
          <p className="font-body text-[#F5F5F0] mt-3">Your documents have been submitted for review. You will be notified by email once verified.</p>
          <p className="font-body text-xs text-[#8A8A82] mt-2">Submitted {new Date(sub.submitted_at).toLocaleString()} · attempt {sub.attempt}</p>
        </div>)}
      {st === "verified" && (
        <div data-testid="kyc-status-verified" className="mt-8 rounded-sm border border-[#2FB999]/50 bg-[#2FB999]/5 p-6 text-center">
          <CheckCircle2 size={22} className="mx-auto text-[#2FB999]" />
          <p className="font-body text-[#F5F5F0] mt-3">Your eKYC has been verified. Please proceed to complete your onboarding.</p>
        </div>)}
      {(st === "rejected" || st === "escalated") && (
        <div data-testid="kyc-status-rejected" className="mt-8 rounded-sm border border-[#B4262A]/50 bg-[#B4262A]/5 p-6">
          <div className="inline-flex items-center gap-2 text-[#E5484D] font-grotesk text-[9px] uppercase tracking-[0.28em]"><AlertTriangle size={13} /> {st === "escalated" ? "Escalated for manual review" : "Correction required"}</div>
          <p className="font-body text-[#F5F5F0] mt-3">Reason: <em>{data.summary.last_reason}</em></p>
          <p className="font-body text-xs text-[#8A8A82] mt-2">{st === "escalated" ? "Self-serve resubmission is closed. A Trade Desk officer will contact you." : "One resubmission is available. Replace the documents that need correction and submit again."}</p>
        </div>)}

      {!locked && (
        <>
          <div className="mt-8 space-y-3">
            {req.documents.map((d) => <DocField key={d.key} api={api} doc={d} uploaded={uploadedBy[d.key]} onDone={(s) => setData((x) => ({ ...x, submission: s }))} />)}
          </div>
          {req.declarations.length > 0 && (
            <div className="mt-5 rounded-sm border border-white/10 bg-black/40 p-5">
              {req.declarations.map((d) => (
                <label key={d.key} className="block">
                  <span className="font-grotesk text-[8px] uppercase tracking-[0.26em] text-[#8A8A82]">{d.label} · optional · self-declared</span>
                  <input data-testid={`kyc-decl-${d.key}`} value={decl[d.key] || ""} onChange={(e) => setDecl({ ...decl, [d.key]: e.target.value })} placeholder={d.placeholder}
                    className="mt-1.5 w-full bg-black/60 border border-white/15 px-3 py-2 font-mono text-sm text-[#F5F5F0] focus:border-[#C9A961] outline-none" />
                </label>))}
            </div>)}
          <label className="mt-6 flex items-start gap-3 cursor-pointer" data-testid="kyc-declaration">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 accent-[#C9A961] w-4 h-4" data-testid="kyc-declaration-checkbox" />
            <span className="font-body text-sm text-[#C9C9C2]">I confirm that all documents uploaded are genuine, valid, and belong to me.</span>
          </label>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <button onClick={submit} disabled={busy || !agree || !mandatoryDone} data-testid="kyc-submit"
              className="inline-flex items-center gap-3 rounded-sm bg-[#C9A961] text-black px-10 py-4 font-grotesk text-xs uppercase tracking-[0.3em] hover:bg-[#E0C078] disabled:opacity-40 transition-colors">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Submit for verification
            </button>
            {allowContinue && (
              <button onClick={onVerifiedOrContinue} data-testid="kyc-continue-later" className={`inline-flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.26em] ${GOLD} hover:underline`}>
                Continue to fee step · complete eKYC later <ArrowRight size={12} />
              </button>)}
          </div>
          {!mandatoryDone && <p className="text-center font-body text-xs text-[#8A8A82] mt-3">Upload every mandatory document to enable submission.</p>}
        </>)}

      {locked && (st === "verified" || allowContinue) && (
        <div className="mt-8 text-center">
          <button onClick={onVerifiedOrContinue} data-testid="kyc-proceed" className="inline-flex items-center gap-3 rounded-sm border border-[#C9A961]/60 text-[#C9A961] px-8 py-3.5 font-grotesk text-xs uppercase tracking-[0.28em] hover:bg-[#C9A961] hover:text-black transition-colors">
            {st === "verified" ? "Proceed to onboarding" : "Continue to fee step"} <ArrowRight size={13} />
          </button>
        </div>)}
    </div>
  );
}
